import { offeredFormatsFromPublicData } from './sessionPricing';
/**
 * Sweet Skills (TEST). Client helpers. Server prices live in server/api-util/sweetSkills.js
 * and are the only source of truth for charges.
 */
export const SKILL_CATEGORIES = [
  { id: 'cooking-baking', label: 'Cooking & baking', emoji: '🍰' },
  { id: 'home-holiday-decorating', label: 'Home & holiday decorating', emoji: '🎃', hint: 'Halloween, holidays, cozy spaces' },
  { id: 'crafts-diy', label: 'Crafts & DIY', emoji: '✂️' },
  { id: 'faith-prayer', label: 'Faith & prayer', emoji: '🙏' },
  { id: 'pets-training', label: 'Pets', emoji: '🐾', hint: 'Puppy and kitten training' },
  { id: 'smart-shopping-deals', label: 'Smart shopping & deals', emoji: '🛍️' },
  // Added Oct 27 2026 (Sam: more Skillz categories, TEST).
  { id: 'fitness-wellness', label: 'Fitness & wellness', emoji: '💪', hint: 'Workouts, yoga, healthy habits' },
  { id: 'business-career', label: 'Business & career', emoji: '💼', hint: 'Resumes, interviews, side hustles' },
  { id: 'music-lessons', label: 'Music lessons', emoji: '🎸', hint: 'Guitar, piano, singing' },
  { id: 'languages', label: 'Languages', emoji: '🗣️', hint: 'Spanish, ASL, conversation practice' },
  { id: 'tech-computers', label: 'Tech & computers', emoji: '💻', hint: 'Phones, AI tools, coding basics' },
  { id: 'tutoring-school', label: 'Tutoring & homework', emoji: '📚', hint: 'Math, reading, test prep' },
  { id: 'art-design', label: 'Art & design', emoji: '🎨', hint: 'Drawing, painting, Canva' },
  { id: 'beauty-style', label: 'Beauty & style', emoji: '💄', hint: 'Makeup, hair, outfits' },
  { id: 'sports-coaching', label: 'Sports coaching', emoji: '⚽', hint: 'Golf swing, basketball, pickleball' },
  { id: 'writing-speaking', label: 'Writing & public speaking', emoji: '✍️', hint: 'Essays, toasts, presentations' },
  { id: 'gardening-plants', label: 'Gardening & plants', emoji: '🌱' },
  { id: 'money-budgeting', label: 'Money & budgeting', emoji: '💵', hint: 'Budgets, saving, credit basics' },
  { id: 'photo-video', label: 'Photo & video', emoji: '📷', hint: 'Phone photos, editing, content creation' },
  { id: 'accessibility-braille', label: 'Braille & accessibility', emoji: '⠃', hint: 'Braille, assistive tech' },
];
// 15 min to 4 h in 15-minute steps; the server validates the same list.
export const SKILL_DURATIONS = Array.from({ length: 16 }, (_, i) => (i + 1) * 15);
export const SKILL_FORMATS = [
  { id: 'chat', label: 'Text' },
  { id: 'voice', label: 'Voice' },
  { id: 'video', label: 'Video' },
];
export const SKILL_PRICE_MIN_USD = 0;
/** Minimums for every offer (listening and skills): text free, voice $5, video $10. */
export const OFFER_PRICE_MIN = { chat: 1, voice: 5, video: 5 }; // Sam 12:19 Oct 23
export const LISTENING_OFFER_ID = 'listening';
/** Listening rates are per 15 minutes; minimums mirror the server (Sam, Oct 22 2026 8:34 PM). */
export const LISTEN_RATE_MIN = { chat: 1, voice: 5, video: 5 };
export const LISTEN_RATE_MAX = 50;
export const LISTEN_MAX_MINUTES = 120;
/** Starter prices for a new length in the editor only; sellers set their own. */
export const STARTER_PRICES_15 = { chat: 10, voice: 20, video: 30 };
export const SKILL_PRICE_MAX_USD = 100000; // Sam Oct 25: no teacher max (sanity ceiling only)
export const MAX_SKILLS = 8;

export const slugifySkill = s =>
  String(s || '').trim().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

// Listening rates per 15 min: $1/$5/$5 minimum, $50 maximum (Sam, Oct 22 2026 8:36 PM).
// Skills: session price may not exceed the $50-per-15-minute cap for its length.
const validPrice = (v, f, listening = false, minutes = 15) => Number.isInteger(v) && (listening ? v >= LISTEN_RATE_MIN[f] && v <= LISTEN_RATE_MAX : v >= (OFFER_PRICE_MIN[f] ?? 5) && v <= SKILL_PRICE_MAX_USD);

export const normalizeOffers = (raw, { withListening = false } = {}) => {
  const list = Array.isArray(raw) ? raw : [];
  const seen = new Set();
  return list
    .map(o => {
      const id = slugifySkill(o?.id || o?.label);
      const label = String(o?.label || '').trim().slice(0, 40);
      if (!id || !label || seen.has(id)) return null;
      if (id === LISTENING_OFFER_ID && !withListening) return null;
      seen.add(id);
      const durations = {};
      SKILL_DURATIONS.forEach(d => {
        const row = o?.durations?.[d] || o?.durations?.[String(d)];
        if (!row) return;
        const clean = {};
        SKILL_FORMATS.forEach(({ id: f }) => {
          if (row[f] === '' || row[f] == null) return;
          let p = Number(row[f]);
          // Listening rates: clamp old-format values (e.g. $0 text) into $1/$5/$5..$50 instead of dropping them.
          if (id === LISTENING_OFFER_ID && Number.isFinite(p)) p = Math.min(LISTEN_RATE_MAX, Math.max(LISTEN_RATE_MIN[f], Math.round(p)));
          if (validPrice(p, f, id === LISTENING_OFFER_ID, d)) clean[f] = p;
        });
        if (Object.keys(clean).length) durations[String(d)] = clean;
      });
      if (!Object.keys(durations).length) return null;
      return id === LISTENING_OFFER_ID ? { id, kind: 'listening', label: 'Listening', durations } : { id, label, durations };
    })
    .filter(Boolean)
    .slice(0, MAX_SKILLS + 1);
};
export const normalizeSkillOffers = raw => normalizeOffers(raw).slice(0, MAX_SKILLS);

/** Listening entry of the one offer list; legacy talkPrices (15 min) if not migrated yet. */
export const listeningOfferFromListing = listing => {
  const pd = listing?.attributes?.publicData || {};
  const own = normalizeOffers(pd.skillOffers, { withListening: true }).find(o => o.id === LISTENING_OFFER_ID);
  if (own) return own;
  // Legacy listing (v99): same clamp as the server; keep only the formats they already offer (Oct 23 2026).
  const offered = offeredFormatsFromPublicData(pd);
  const legacy = {};
  ['chat', 'voice', 'video'].forEach(f => {
    if (!offered.includes(f)) return;
    const raw = pd.talkPrices?.[f];
    const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
    legacy[f] = typeof n === 'number' && Number.isFinite(n) ? Math.min(LISTEN_RATE_MAX, Math.max(LISTEN_RATE_MIN[f], Math.round(n))) : STARTER_PRICES_15[f];
  });
  return { id: LISTENING_OFFER_ID, kind: 'listening', label: 'Listening', durations: { 15: legacy } };
};

/** "What I offer": Listening first, then skills (hidden while Teaching is off). */
/** Bookable listening: rate per 15 min x blocks (server prices the same way). */
export const listeningBookableOffer = listing => {
  const o = listeningOfferFromListing(listing);
  const rate = o.durations['15'] || {};
  const durations = {};
  SKILL_DURATIONS.filter(m => m <= LISTEN_MAX_MINUTES).forEach(m => {
    durations[m] = Object.fromEntries(Object.entries(rate).map(([f, p]) => [f, p * (m / 15)]));
  });
  return { ...o, durations, rate };
};
export const offerMenuFromListing = listing => [listeningBookableOffer(listing), ...skillOffersFromListing(listing)];
export const isListeningOffer = o => o?.id === LISTENING_OFFER_ID;

/** Public skills: hidden when the listener switched Teaching off. */
export const skillOffersFromListing = listing =>
  listing?.attributes?.publicData?.teachingOn === false
    ? []
    : normalizeSkillOffers(listing?.attributes?.publicData?.skillOffers);

/** Owner's saved skills, even while Teaching is switched off. */
export const ownSkillOffers = listing => normalizeSkillOffers(listing?.attributes?.publicData?.skillOffers);

/** Lowest price + shortest length, for chips: "Cooking & baking · from $12". */
export const offerFromPrice = offer => {
  let min = null;
  Object.values(offer?.durations || {}).forEach(row =>
    Object.values(row).forEach(p => {
      if (min == null || p < min) min = p;
    })
  );
  return min;
};

export const categoryForSkill = id => SKILL_CATEGORIES.find(c => c.id === id) || null;

const LEARN_NOTE_KEY = 'SweetSkillsLearnNote:';
export const saveLearnNote = (listingId, note) => {
  try {
    window.sessionStorage.setItem(`${LEARN_NOTE_KEY}${listingId}`, JSON.stringify(note));
  } catch (e) {
    // private mode
  }
};
export const readLearnNote = listingId => {
  try {
    return JSON.parse(window.sessionStorage.getItem(`${LEARN_NOTE_KEY}${listingId}`) || 'null');
  } catch (e) {
    return null;
  }
};

export const priceLabel = usd => (usd === 0 ? 'Free' : `$${usd}`);
